import React from "react";
import { Ban, BookCheck } from "lucide-react";
import { useThreadsList } from "./useThreadsList";
import { Input } from "components/shadcn/ui/input";
import { Switch } from "components/shadcn/ui/switch";
import { Badge } from "components/shadcn/ui/badge";
import { Avatar, AvatarFallback } from "components/shadcn/ui/avatar";
import { Card, CardContent } from "components/shadcn/ui/card";
import { getInitials, getTimeLabelWithDay } from "helpers";
import ToolTipWrapper from "components/common/helper/tooltipWrapper";
import { Thread } from "../types";
import { cn } from "components/lib/utils";
import ConfirmDialog from "components/common/helper/confirmDialogue";
import Loader from "components/common/loader";

const ThreadsList: React.FC = () => {
  const {
    threads,
    loading,
    search,
    setSearch,
    showArchived,
    setShowArchived,
    handleSelectedThread,
    selectedThreadId,
    handleArchiveThread,
    blockTarget,
    setBlockTarget,
    confirmBlockToggle,
  } = useThreadsList();

  const pinnedThreads = threads.filter((thread) => thread.isPinned);
  const normalThreads = threads.filter((thread) => !thread.isPinned);

  const noSearchResults = !loading && threads.length === 0 && search.length > 0;

  const noArchivedResults =
    !loading && threads.length === 0 && showArchived && search.length === 0;

  const noActiveChats =
    !loading && threads.length === 0 && search.length === 0 && !showArchived;

  const renderThread = (thread: Thread) => {
    const isSelected = selectedThreadId === thread.id;

    return (
      <div
        key={thread.id}
        className="relative mx-3 mb-2"
        onClick={() => handleSelectedThread(thread.id)}
      >
        <ConfirmDialog
          open={!!blockTarget}
          onOpenChange={() => setBlockTarget(null)}
          title={
            blockTarget?.blockContext.blockedByMe
              ? "Unblock user"
              : "Block user"
          }
          description={
            blockTarget?.blockContext.blockedByMe
              ? "You will be able to send messages again."
              : "You won’t receive messages from this user."
          }
          confirmLabel={
            blockTarget?.blockContext.blockedByMe ? "Unblock" : "Block"
          }
          confirmVariant="primary"
          onConfirm={confirmBlockToggle}
          showCancelButton
        />

        {isSelected && (
          <div className="absolute left-0 top-0 h-full w-1 bg-[var(--primary-700)] rounded-l-md" />
        )}

        <Card
          className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            isSelected ? "bg-gray-100 shadow-sm" : "hover:bg-blue-50"
          )}
        >
          <CardContent className="p-3 flex items-center gap-3">
            <div className="flex flex-col gap-2">
              <ToolTipWrapper
                content={thread.isArchived ? `Unarchive` : `Archive`}
              >
                <BookCheck
                  size={16}
                  className={`cursor-pointer transition-colors ${
                    thread.isArchived
                      ? "text-[var(--primary-700)]"
                      : "text-gray-700 hover:text-[var(--primary-700)]"
                  }`}
                  stroke={
                    thread.isArchived ? "var(--primary-700)" : "currentColor"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchiveThread(thread.id, thread.isArchived);
                  }}
                />
              </ToolTipWrapper>

              <ToolTipWrapper
                content={
                  thread.blockContext.blockedByMe
                    ? "Unblock user"
                    : "Block user"
                }
              >
                <Ban
                  size={16}
                  className={`cursor-pointer transition-colors ${
                    thread.blockContext.blockedByMe
                      ? "text-red-600"
                      : "text-gray-700 hover:text-red-600"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBlockTarget(thread);
                  }}
                />
              </ToolTipWrapper>
            </div>

            <Avatar>
              <AvatarFallback>
                {getInitials(
                  thread.participant.firstName,
                  thread.participant.lastName
                )}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  "text-sm truncate text-left",
                  isSelected ? "font-semibold" : "font-medium"
                )}
              >
                {thread.participant.firstName} {thread.participant.lastName}
              </p>
              <p className="text-sm text-gray-500 truncate text-left">
                {thread.lastMessage}
              </p>
            </div>

            <div className="flex flex-col items-end gap-1">
              {thread.lastMessageAt && (
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {getTimeLabelWithDay(thread.lastMessageAt)}
                </span>
              )}

              {thread.unreadCount > 0 && (
                <Badge variant="destructive">{thread.unreadCount}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 flex flex-row gap-2 items-center">
        <Input
          placeholder="Search user..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="flex flex-row gap-2 items-center justify-between">
          <BookCheck size={20} className="text-gray-700 " />
          <ToolTipWrapper
            content={showArchived ? "Hide Archived" : "Show Archived"}
          >
            <Switch checked={showArchived} onCheckedChange={setShowArchived} />
          </ToolTipWrapper>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <>
            <Loader />
          </>
        )}

        {!loading && pinnedThreads.length > 0 && (
          <>
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase text-left">
              Pinned
            </p>
            {pinnedThreads.map(renderThread)}
          </>
        )}

        {!loading && normalThreads.length > 0 && (
          <>
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase text-left">
              All Messages
            </p>
            {normalThreads.map(renderThread)}
          </>
        )}

        {noActiveChats && (
          <p className="mt-10 text-center text-sm text-gray-400">
            No Active chat found
          </p>
        )}

        {noSearchResults && (
          <p className="mt-10 text-center text-sm text-gray-400">
            No user found
          </p>
        )}

        {noArchivedResults && (
          <p className="mt-10 text-center text-sm text-gray-400">
            No archived chat found
          </p>
        )}
      </div>
    </div>
  );
};

export default ThreadsList;
